"use client";

import React from "react";
import { Phone, Mail, Wrench, HardHat, Loader, CircleCheck, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="81712" className="flex flex-col items-center justify-center p-12 border-2 border-secondary/30 bg-secondary/5 text-center space-y-4 rounded-3xl backdrop-blur-sm">
        <CircleCheck className="size-16 text-secondary" />
        <h3 className="text-2xl font-serif font-semibold text-foreground">
          Message Received
        </h3>
        <p className="text-muted-foreground font-sans">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6 rounded-full border-2 border-secondary text-secondary hover:bg-secondary/10 transition-all duration-300 font-sans font-medium tracking-wide">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="name" rules={{
          required: "Name is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-sm font-medium tracking-wide text-foreground/80 font-sans">
                  Your Name
                </FormLabel>
                <FormControl>
                  <Input placeholder="Your Name" className="rounded-xl border-border bg-background/50 text-foreground placeholder:text-muted-foreground/50 focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary h-12 transition-all" {...field} />
                </FormControl>
                <FormDescription className="text-[10px] uppercase font-sans tracking-tight text-muted-foreground/60">
                  Full name of the primary contact
                </FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="phone" rules={{
          required: "Phone number is required",
          pattern: {
            value: /^[0-9\-+() ]+$/,
            message: "Invalid phone number format"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-sm font-medium tracking-wide text-foreground/80 font-sans">
                  Phone Number
                </FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="Your Phone" className="rounded-xl border-border bg-background/50 text-foreground placeholder:text-muted-foreground/50 focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary h-12 transition-all" {...field} />
                </FormControl>
                <FormDescription className="text-[10px] uppercase font-sans tracking-tight text-muted-foreground/60">
                  Best number to reach you
                </FormDescription>
                <FormMessage />
              </FormItem>} />
        </div>

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-sm font-medium tracking-wide text-foreground/80 font-sans">
                Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="your@email.com" className="rounded-xl border-border bg-background/50 text-foreground placeholder:text-muted-foreground/50 focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary h-12 transition-all" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" rules={{
        required: "Message is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-sm font-medium tracking-wide text-foreground/80 font-sans">
                Tell us about your family's needs
              </FormLabel>
              <FormControl>
                <Textarea placeholder="How can we help you or your loved one?" className="rounded-xl border-border bg-background/50 text-foreground placeholder:text-muted-foreground/50 focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary min-h-[160px] resize-y transition-all" {...field} />
              </FormControl>
              <FormDescription className="text-[10px] uppercase font-sans tracking-tight text-muted-foreground/60">
                Please share any specific care needs or questions about our home
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="flex items-center gap-3 p-4 border-2 border-destructive/50 bg-destructive/10 text-destructive rounded-xl">
            <CircleAlert className="size-5 shrink-0" />
            <p className="text-sm font-sans">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full rounded-full h-16 bg-secondary hover:bg-secondary/90 text-secondary-foreground text-lg font-semibold tracking-wide transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-3 size-6" />
              Sending...
            </> : "Contact Family First Senior Care"}
        </Button>
      </div>
    </Form>;
}
export default function ContactUs() {
  return <section id="get-in-touch" className="w-full bg-background py-24 relative overflow-hidden text-foreground">
      {}
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
      backgroundImage: 'radial-gradient(circle at 2px 2px, hsl(var(--primary)) 1px, transparent 0)',
      backgroundSize: '40px 40px'
    }} />

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {}
          <div className="lg:col-span-10 lg:col-start-2 xl:col-span-8 xl:col-start-3">
            
            {}
            <div className="border-b border-border pb-6 mb-10 flex flex-col md:flex-row md:items-end justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 mb-4 text-primary">
                  <Wrench className="size-6 opacity-40" />
                  <HardHat className="size-6 opacity-40" />
                </div>
                <h2 className="text-4xl md:text-5xl font-serif font-semibold tracking-tight text-foreground">
                  A Warm Welcome at Family First Senior Care
                </h2>
              </div>
              <div className="text-left md:text-right">
                <p className="text-primary font-sans text-sm uppercase tracking-widest font-medium">
                  Personalized Care for up to 5 Residents
                </p>
                <p className="text-muted-foreground font-sans text-xs mt-1">
                  Our Atmosphere: <span className="text-foreground">Intimate & Family-Like</span>
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
              {}
              <div className="md:col-span-4 flex flex-col gap-8">
                <div>
                  <h3 className="text-lg font-sans font-medium uppercase tracking-wider text-foreground mb-4">
                    Connect With Us
                  </h3>
                  <div className="space-y-6">
                    <div className="flex items-start gap-4 group">
                      <div className="p-3 rounded-2xl border border-border group-hover:border-primary/30 group-hover:bg-primary/5 transition-all duration-300">
                        <Phone className="size-6 text-primary transition-colors" strokeWidth={1.5} />
                      </div>
                      <div>
                        <p className="text-xs font-sans font-semibold uppercase tracking-widest text-muted-foreground mb-1">
                          Admissions & Care
                        </p>
                        <p className="text-foreground font-sans font-medium">
                          301-477-9779
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4 group">
                      <div className="p-3 rounded-2xl border border-border group-hover:border-primary/30 group-hover:bg-primary/5 transition-all duration-300">
                        <Mail className="size-6 text-primary transition-colors" strokeWidth={1.5} />
                      </div>
                      <div>
                        <p className="text-xs font-sans font-semibold uppercase tracking-widest text-muted-foreground mb-1">
                          Direct Email
                        </p>
                        <p className="text-foreground font-sans font-medium">info@familyfirstassistedliving.org</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-auto pt-8 border-t border-border">
                  <p className="text-xs font-sans text-muted-foreground leading-relaxed italic">
                    As a boutique home, we provide personalized attention to every inquiry. We will personally reach out to your family within 24 hours to discuss your needs. We proudly serve families in Silver Spring, Maryland, and throughout Montgomery County.
                  </p>
                  <p className="text-xs font-sans text-muted-foreground leading-relaxed mt-4">
                    We invite you to experience our boutique care firsthand. Please call 301-477-9779 or schedule a tour to visit our home and meet our dedicated team.
                  </p>
                </div>
              </div>

              {}
              <div className="md:col-span-8 bg-card p-6 md:p-10 border border-border rounded-3xl shadow-xl shadow-foreground/5">
                <FormProvider formKey="get_in_touch_form_contact" formId="get_in_touch_form_contact" sectionName="Contact Us" defaultValues={{
                name: "",
                email: "",
                phone: "",
                message: ""
              }} submitText="Contact Family First Senior Care" successMessage="Thank you for reaching out. Our small care team will contact you personally to discuss how we can support your loved one in our family-like home. Our team in Silver Spring, Maryland, Montgomery County is ready to assist you.">
                  <ContactFormContent />
                </FormProvider>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>;
}